/**
 * EditablePlaybookViewer
 *
 * Rich playbook viewer: per-step cache/locator status, selector badges, step
 * screenshots, branch-aware rendering, and per-node cache controls.
 *
 * Editing (drag-reorder, add/delete step, inline edit) is gated on the
 * `onUpdateNode` callback. Playbooks are now an AGENT-OWNED artifact — human
 * editing has been removed — so the PlaybookOverlay renders this WITHOUT
 * `onUpdateNode`, i.e. read-only. The editing affordances remain here behind
 * that gate (no consumer passes the callback today).
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, Robot, Trash, CheckCircle, Lightning, Sparkle } from '@phosphor-icons/react';
import {
  Text,
  Flex,
  TextField,
  Select,
  cn,
  type SelectOption,
  type PlaybookBranchInfo,
} from '@chatsdet/ui';
import {
  NODE_TYPE_LABEL,
  PLAYBOOK_STEP_ACTIONS,
  type LegacyPlaybookStepAction,
  type PlaybookStep,
  type PlaybookStepAction,
} from '@chatsdet/types';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import type { EffectivePlaybookData, NodeCacheData, PlaybookState } from '../types/playbook-cache';
import { usePlaybookDragDrop, type FlatPlaybookItem } from '../hooks/usePlaybookDragDrop';
import { SortablePlaybookRow } from './SortablePlaybookRow';
import { EffectivePlaybookSummary } from './EffectivePlaybookSummary';
import { API_URL } from '../../../../config';
import {
  getPrimarySelectorType,
  formatSelectorValue,
  selectorTypeBadgeColor,
} from '../selector-format';

/**
 * Resolve a playbook step's cache state from the node's cache row: whether the
 * step replays deterministically (cached) or runs live (gap), and its primary
 * locator. Returns null when there's no compiled entry for the step.
 */
function resolveStepCache(
  cacheByNodeId: Map<string, NodeCacheData> | undefined,
  nodeId: string,
  step: PlaybookStep
): { entry: 'cached' | 'gap'; selectors: Record<string, unknown> | null } | null {
  const nodeCache = cacheByNodeId?.get(nodeId);
  if (!nodeCache?.compiledEntries) return null;
  const ce = nodeCache.compiledEntries.find((e) => e.stepOrder === step.order);
  if (!ce) return null;
  if (ce.type === 'gap') return { entry: 'gap', selectors: null };
  // compiledEntries actionIndices are array positions into actions[].
  const idx = ce.actionIndices[0];
  const action = idx !== undefined ? nodeCache.actions[idx] : undefined;
  return {
    entry: 'cached',
    selectors: (action?.selectors as Record<string, unknown> | undefined) ?? null,
  };
}

/** Compact per-step cache/locator line shown under a playbook row. */
function StepCacheRow({ info }: { info: ReturnType<typeof resolveStepCache> }) {
  if (!info) return null;
  if (info.entry === 'gap') {
    return (
      <div className="flex items-center gap-1 px-3 pb-1.5 -mt-0.5">
        <Lightning size={11} weight="fill" className="text-amber-500 shrink-0" />
        <span
          className="text-[10px] text-amber-600 dark:text-amber-400"
          title="Runs live during replay (no cached locator)"
        >
          gap · runs live
        </span>
      </div>
    );
  }
  const type = getPrimarySelectorType(info.selectors);
  const display = formatSelectorValue(info.selectors);
  return (
    <div className="flex items-center gap-1.5 px-3 pb-1.5 -mt-0.5 min-w-0">
      <CheckCircle size={11} weight="fill" className="text-blue-500 shrink-0" />
      <span className="text-[10px] text-muted-foreground shrink-0">cached</span>
      {type && (
        <span
          className={cn(
            'px-1 py-0.5 rounded text-[9px] font-medium shrink-0',
            selectorTypeBadgeColor[type]
          )}
        >
          {type}
        </span>
      )}
      <code className="text-[10px] font-mono text-muted-foreground truncate" title={display}>
        {display}
      </code>
    </div>
  );
}

const CHIP = 'text-[10px] font-medium px-1.5 py-0.5 rounded';

/** Playbook lifecycle state → badge styling + tooltip. */
const LIFECYCLE_BADGE: Record<PlaybookState, { label: string; cls: string; title: string }> = {
  verified: {
    label: 'Verified',
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    title: 'Playbook proven — eligible for deterministic replay',
  },
  suspect: {
    label: 'Suspect',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    title: 'A verified playbook that needed L3 rescue — one clean run to re-verify',
  },
  retired: {
    label: 'Retired',
    cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    title: 'Playbook busted — the next run re-authors a fresh one (not given to the agent)',
  },
  unverified: {
    label: 'Unverified',
    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    title: 'Not yet proven — runs in L2 until it earns verified',
  },
};

/**
 * Per-node header chips for the cache row: lifecycle state, validity, source,
 * compilation score, cached/gap tally, and optimizer provenance. Renders what
 * the agent reads from the cache row (cache-row-first), so a retired/invalid row
 * is visible and badged rather than hidden.
 */
function NodeCacheSummary({ cache }: { cache?: NodeCacheData }) {
  if (!cache) return null;

  const entries = cache.compiledEntries ?? [];
  const cached = entries.filter((e) => e.type === 'cached').length;
  const gaps = entries.filter((e) => e.type === 'gap').length;
  const optimized = cache.repairSource === 'cache-optimizer';
  const lifecycle = cache.playbookState ? LIFECYCLE_BADGE[cache.playbookState] : null;
  const streak = cache.playbookSuccessStreak ?? 0;
  const showInvalid = cache.isValid === false;
  const fromConfig = cache.playbookSource === 'config_fallback';
  const score = cache.compilationScore;
  const hasTally = cached > 0 || gaps > 0;

  if (!lifecycle && !showInvalid && !fromConfig && score == null && !hasTally && !optimized) {
    return null;
  }

  const tallyLabel =
    gaps > 0 ? `${cached} cached · ${gaps} gap${gaps === 1 ? '' : 's'}` : `${cached} cached`;

  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      {lifecycle && (
        <span className={cn(CHIP, lifecycle.cls)} title={lifecycle.title}>
          {lifecycle.label}
          {cache.playbookState === 'unverified' && streak > 0 ? ` · ${streak}` : ''}
        </span>
      )}
      {showInvalid && (
        <span
          className={cn(CHIP, 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300')}
          title="This cache row was invalidated (evicted/healed); it will re-prove on the next run"
        >
          Invalid
        </span>
      )}
      {fromConfig && (
        <span
          className={cn(CHIP, 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}
          title="The cache row has no authored playbook yet — showing the node's config playbook (the agent falls back to it)"
        >
          from config
        </span>
      )}
      {score != null && (
        <span
          className={cn(
            CHIP,
            'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
          )}
          title="Cache compiler score for this row"
        >
          score {Math.round(score)}
        </span>
      )}
      {optimized && (
        <span
          className={cn(
            CHIP,
            'inline-flex items-center gap-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
          )}
          title="Authored by the cache optimizer from a recent run"
        >
          <Sparkle size={10} weight="fill" />
          Optimized
        </span>
      )}
      {hasTally && (
        <span
          className={cn(
            CHIP,
            gaps > 0
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
          )}
        >
          {tallyLabel}
        </span>
      )}
    </span>
  );
}

const NODE_TYPE_COLORS: Record<string, string> = {
  login: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  navigate: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  action: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  assertion: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  wait: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  setup: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  condition: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
};

const ACTION_OPTIONS: SelectOption[] = PLAYBOOK_STEP_ACTIONS.map((a) => ({
  id: a,
  label: a.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
}));

const POPOVER_WIDTH = 420;

function computePopoverPosition(
  anchorRect: DOMRect,
  popoverWidth: number,
  estimatedHeight: number
) {
  const gap = 8;
  const spaceRight = window.innerWidth - anchorRect.right;
  const placeRight = spaceRight >= popoverWidth + gap;
  const left = placeRight ? anchorRect.right + gap : anchorRect.left - popoverWidth - gap;
  const top = Math.max(
    8,
    Math.min(
      anchorRect.top - estimatedHeight / 2 + anchorRect.height / 2,
      window.innerHeight - estimatedHeight - 8
    )
  );
  return { left, top };
}

interface PopoverFooterProps {
  step: PlaybookStep;
  nodeId: string;
  cacheByNodeId?: Map<string, NodeCacheData>;
  onDeleteNodeCache?: (nodeId: string) => Promise<void>;
}

function PopoverCacheFooter({
  step,
  nodeId,
  cacheByNodeId,
  onDeleteNodeCache,
}: PopoverFooterProps) {
  const nodeCache = cacheByNodeId?.get(nodeId);

  // Find AI-generated locator: prefer step.actionRefs, fall back to compiledEntries
  const _aiLocator: string | null = null;
  let actionIndices: number[] = step.actionRefs ?? [];
  // actionRefs stores real action .index values; compiledEntries stores array positions
  let useArrayPosition = false;
  if (actionIndices.length === 0 && nodeCache?.compiledEntries) {
    const entry = nodeCache.compiledEntries.find(
      (e) => e.stepOrder === step.order && e.type === 'cached'
    );
    if (entry && entry.type === 'cached') {
      actionIndices = entry.actionIndices;
      useArrayPosition = true;
    }
  }

  // Collect locators: single for most actions, per-field for fill_form
  let aiLocators: string[] = [];
  if (actionIndices.length > 0 && nodeCache) {
    for (const actionIndex of actionIndices) {
      const cachedAction = useArrayPosition
        ? nodeCache.actions[actionIndex]
        : nodeCache.actions.find((a) => a.index === actionIndex);
      if (cachedAction?.selectorAuthor === 'agentic') {
        if (cachedAction.selectors?.playwrightLocator) {
          aiLocators = [cachedAction.selectors.playwrightLocator];
          break;
        }
        // fill_form: per-field selectors in fields[N].selectors
        const fields = (
          cachedAction.args as {
            fields?: Array<{ label?: string; selectors?: { playwrightLocator?: string } }>;
          }
        )?.fields;
        if (fields?.length) {
          const fieldLocators = fields
            .filter((f) => f.selectors?.playwrightLocator)
            .map((f) => `${f.label ?? 'field'}: ${f.selectors!.playwrightLocator!}`);
          if (fieldLocators.length > 0) {
            aiLocators = fieldLocators;
            break;
          }
        }
      }
    }
  }

  if (aiLocators.length === 0 && !nodeCache) return null;

  return (
    <div className="border-t border-border px-3 py-2 space-y-1.5">
      {aiLocators.map((loc, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Robot size={12} className="text-amber-500 shrink-0" />
          <code className="text-[10px] font-mono text-muted-foreground truncate">{loc}</code>
        </div>
      ))}
      {nodeCache && onDeleteNodeCache && (
        <button
          type="button"
          onClick={() => onDeleteNodeCache(nodeId)}
          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash size={11} />
          Clear cache for this step
        </button>
      )}
    </div>
  );
}

interface ScreenshotPopoverProps {
  screenshotUrl: string;
  anchorRect: DOMRect;
  step: PlaybookStep;
  nodeId: string;
  cacheByNodeId?: Map<string, NodeCacheData>;
  onDeleteNodeCache?: (nodeId: string) => Promise<void>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function ScreenshotPopover({
  screenshotUrl,
  anchorRect,
  step,
  nodeId,
  cacheByNodeId,
  onDeleteNodeCache,
  onMouseEnter,
  onMouseLeave,
}: ScreenshotPopoverProps) {
  const { left, top } = computePopoverPosition(anchorRect, POPOVER_WIDTH, 320);
  return createPortal(
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left: `${left}px`, top: `${top}px`, width: `${POPOVER_WIDTH}px` }}
    >
      <div
        className="rounded-lg border border-border bg-card shadow-lg overflow-hidden pointer-events-auto"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <img src={screenshotUrl} alt="Step screenshot" className="w-full h-auto" loading="eager" />
        <PopoverCacheFooter
          step={step}
          nodeId={nodeId}
          cacheByNodeId={cacheByNodeId}
          onDeleteNodeCache={onDeleteNodeCache}
        />
      </div>
    </div>,
    document.body
  );
}

interface NoScreenshotPopoverProps {
  anchorRect: DOMRect;
  step: PlaybookStep;
  nodeId: string;
  cacheByNodeId?: Map<string, NodeCacheData>;
  onDeleteNodeCache?: (nodeId: string) => Promise<void>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function NoScreenshotPopover({
  anchorRect,
  step,
  nodeId,
  cacheByNodeId,
  onDeleteNodeCache,
  onMouseEnter,
  onMouseLeave,
}: NoScreenshotPopoverProps) {
  const placeholderHeight = 120;
  const { left, top } = computePopoverPosition(anchorRect, POPOVER_WIDTH, placeholderHeight + 60);
  return createPortal(
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left: `${left}px`, top: `${top}px`, width: `${POPOVER_WIDTH}px` }}
    >
      <div
        className="rounded-lg border border-border bg-card shadow-lg overflow-hidden pointer-events-auto"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className="flex flex-col items-center justify-center gap-1.5 text-muted-foreground"
          style={{ height: `${placeholderHeight}px` }}
        >
          <span className="text-2xl opacity-40">&#128247;</span>
          <span className="text-xs">No screenshot yet</span>
          <span className="text-[10px] opacity-60">Will capture on next run</span>
        </div>
        <PopoverCacheFooter
          step={step}
          nodeId={nodeId}
          cacheByNodeId={cacheByNodeId}
          onDeleteNodeCache={onDeleteNodeCache}
        />
      </div>
    </div>,
    document.body
  );
}

function getNodeTitle(node: WorkflowNode): string {
  const data = (node.data ?? {}) as Record<string, unknown>;
  if (data.label) return data.label as string;
  if (data.customActionTitle) return data.customActionTitle as string;
  switch (node.type) {
    case 'login':
      return (data.credentialName as string) || NODE_TYPE_LABEL.login;
    case 'navigate':
      return (data.pageTitle as string) || (data.url as string) || 'Navigate';
    case 'action':
      return (data.actionDescription as string) || 'Action';
    case 'assertion':
      return (data.description as string) || 'Assertion';
    case 'wait':
      return (data.description as string) || 'Wait';
    case 'condition':
      return (data.conditionDescription as string) || 'Condition';
    case 'setup':
      return (data.setupFlowName as string) || 'Setup';
    default:
      return node.type || 'Step';
  }
}

interface InlineEditState {
  itemId: string;
  action: LegacyPlaybookStepAction;
  target: string;
  note: string;
  params: Array<{ key: string; value: string }>;
}

function BranchDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      <div className="flex-1 border-t border-dashed border-border" />
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <div className="flex-1 border-t border-dashed border-border" />
    </div>
  );
}

/**
 * Build branch info map from edges (same logic as PlaybookOverlay).
 */
function buildBranchInfoMap(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): Map<string, PlaybookBranchInfo> {
  const result = new Map<string, PlaybookBranchInfo>();
  const nodeSet = new Set(nodes.map((n) => n.id));

  const outgoing = new Map<string, Map<string, string[]>>();
  for (const edge of edges) {
    if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) continue;
    if (!outgoing.has(edge.source)) outgoing.set(edge.source, new Map());
    const sourceMap = outgoing.get(edge.source)!;
    const edgeType = edge.edgeType || 'default';
    if (!sourceMap.has(edgeType)) sourceMap.set(edgeType, []);
    sourceMap.get(edgeType)!.push(edge.target);
  }

  function findAllReachable(startNodes: string[]): Set<string> {
    const visited = new Set<string>();
    const stack = [...startNodes];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (visited.has(id)) continue;
      visited.add(id);
      const edges = outgoing.get(id);
      if (!edges) continue;
      for (const targets of edges.values()) {
        for (const t of targets) {
          if (!visited.has(t)) stack.push(t);
        }
      }
    }
    return visited;
  }

  const conditionNodes = nodes.filter((n) => n.type === 'condition');
  for (const condNode of conditionNodes) {
    const nodeEdges = outgoing.get(condNode.id);
    if (!nodeEdges) continue;

    const explicitTrue = nodeEdges.get('condition_true') ?? [];
    const defaultEdges = nodeEdges.get('default') ?? [];
    const trueTargets = explicitTrue.length > 0 ? explicitTrue : defaultEdges;
    const falseTargets = nodeEdges.get('condition_false') ?? [];

    const trueReachable = findAllReachable(trueTargets);
    const falseReachable = findAllReachable(falseTargets);

    const trueExclusive = new Set<string>();
    for (const id of trueReachable) {
      if (!falseReachable.has(id)) trueExclusive.add(id);
    }
    const falseExclusive = new Set<string>();
    for (const id of falseReachable) {
      if (!trueReachable.has(id)) falseExclusive.add(id);
    }

    if (!result.has(condNode.id)) {
      result.set(condNode.id, {
        type: 'branch-start',
        trueBranchNodeIds: [...trueExclusive],
        falseBranchNodeIds: [...falseExclusive],
      });
    }
    for (const id of trueExclusive) {
      if (!result.has(id)) {
        result.set(id, { type: 'branch-child', branchLabel: 'true', conditionNodeId: condNode.id });
      }
    }
    for (const id of falseExclusive) {
      if (!result.has(id)) {
        result.set(id, {
          type: 'branch-child',
          branchLabel: 'false',
          conditionNodeId: condNode.id,
        });
      }
    }
    for (const id of trueReachable) {
      if (falseReachable.has(id) && !result.has(id)) {
        result.set(id, { type: 'convergence' });
      }
    }
  }

  return result;
}

/**
 * Topological sort of nodes using edges to determine execution order.
 * Falls back to the original nodes array order if edges are absent.
 */
function topoSortNodes(nodes: WorkflowNode[], edges?: WorkflowEdge[]): WorkflowNode[] {
  if (!edges || edges.length === 0) return nodes;

  const nodeSet = new Set(nodes.map((n) => n.id));
  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();

  for (const node of nodes) {
    inDegree.set(node.id, 0);
    adjacency.set(node.id, []);
  }

  for (const edge of edges) {
    if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) continue;
    adjacency.get(edge.source)!.push(edge.target);
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
  }

  // BFS (Kahn's algorithm)
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    sorted.push(id);
    for (const neighbor of adjacency.get(id) ?? []) {
      const newDeg = (inDegree.get(neighbor) ?? 1) - 1;
      inDegree.set(neighbor, newDeg);
      if (newDeg === 0) queue.push(neighbor);
    }
  }

  const orderMap = new Map(sorted.map((id, i) => [id, i]));
  return [...nodes].sort(
    (a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity)
  );
}

/**
 * Compute indentation depth for branch-aware rendering.
 */
function computeNodeBranchDepths(
  nodes: WorkflowNode[],
  branchInfoMap: Map<string, PlaybookBranchInfo>
): Map<string, number> {
  const depths = new Map<string, number>();
  let currentDepth = 0;
  const depthStack: number[] = [];

  for (const node of nodes) {
    const info = branchInfoMap.get(node.id);
    if (info?.type === 'convergence') {
      currentDepth = depthStack.length > 0 ? depthStack.pop()! : 0;
      depths.set(node.id, currentDepth);
    } else if (info?.type === 'branch-start') {
      depths.set(node.id, currentDepth);
      depthStack.push(currentDepth);
      currentDepth += 1;
    } else {
      depths.set(node.id, currentDepth);
    }
  }

  return depths;
}

interface EditablePlaybookViewerProps {
  nodes: WorkflowNode[];
  edges?: WorkflowEdge[];
  /**
   * When provided, enables editing (drag-reorder, add/delete, inline edit).
   * Omit for the read-only view — playbooks are agent-owned and no longer
   * human-editable.
   */
  onUpdateNode?: (nodeId: string, data: Record<string, unknown>) => Promise<void>;
  cacheByNodeId?: Map<string, NodeCacheData>;
  effectiveByNodeId?: Map<string, EffectivePlaybookData>;
  onDeleteNodeCache?: (nodeId: string) => Promise<void>;
  onNodeNavigate?: (nodeId: string) => void;
  scrollToNodeId?: string | null;
}

export function EditablePlaybookViewer({
  nodes,
  edges,
  onUpdateNode,
  cacheByNodeId,
  effectiveByNodeId,
  onDeleteNodeCache,
  onNodeNavigate,
  scrollToNodeId,
}: EditablePlaybookViewerProps) {
  const editable = Boolean(onUpdateNode);

  // Sort nodes in execution order (topological sort based on edges)
  const sortedNodes = React.useMemo(() => topoSortNodes(nodes, edges), [nodes, edges]);

  const { flatItems, sensors, handleDragEnd, sortableIds } = usePlaybookDragDrop({
    nodes: sortedNodes,
    // Read-only when no edit callback; the drag handle is hidden so this no-op
    // is never reached, but the hook requires a function.
    onUpdateNode: onUpdateNode ?? (async () => {}),
  });

  const branchInfoMap = React.useMemo(
    () =>
      edges && edges.length > 0
        ? buildBranchInfoMap(sortedNodes, edges)
        : new Map<string, PlaybookBranchInfo>(),
    [sortedNodes, edges]
  );

  const branchDepths = React.useMemo(
    () => computeNodeBranchDepths(sortedNodes, branchInfoMap),
    [sortedNodes, branchInfoMap]
  );

  const [editState, setEditState] = useState<InlineEditState | null>(null);
  const [pendingEditStep, setPendingEditStep] = useState<{ nodeId: string; order: number } | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const nodeHeaderRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  // Resolve pending edit after flatItems updates with the new step
  useEffect(() => {
    if (!pendingEditStep) return;
    const match = flatItems.find(
      (fi) => fi.nodeId === pendingEditStep.nodeId && fi.step.order === pendingEditStep.order
    );
    if (match) {
      setPendingEditStep(null);
      setEditState({
        itemId: match.id,
        action: match.step.action,
        target: match.step.target,
        note: match.step.note ?? '',
        params: [],
      });
    }
  }, [flatItems, pendingEditStep]);

  // Auto-scroll to target node header when overlay opens
  useEffect(() => {
    if (!scrollToNodeId) return;
    // Wait for DOM to settle after overlay animation
    const timer = setTimeout(() => {
      const el = nodeHeaderRefs.current.get(scrollToNodeId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [scrollToNodeId]);

  const handleHover = useCallback((itemId: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredItemId(itemId), 100);
  }, []);

  const handleLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHoveredItemId(null), 150);
  }, []);

  const handlePopoverEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  const handleSelect = useCallback(
    (item: FlatPlaybookItem) => {
      if (!editable) return;
      if (editState?.itemId === item.id) return;
      const params = item.step.params
        ? Object.entries(item.step.params).map(([key, value]) => ({ key, value }))
        : [];
      setEditState({
        itemId: item.id,
        action: item.step.action,
        target: item.step.target,
        note: item.step.note ?? '',
        params,
      });
    },
    [editable, editState?.itemId]
  );

  const saveEdit = useCallback(async () => {
    if (!onUpdateNode) return;
    if (!editState) return;
    const item = flatItems.find((fi) => fi.id === editState.itemId);
    if (!item) return;

    const paramsObj: Record<string, string> = {};
    for (const p of editState.params) {
      if (p.key.trim()) paramsObj[p.key] = p.value;
    }

    const updatedStep: PlaybookStep = {
      ...item.step,
      action: editState.action,
      target: editState.target,
      note: editState.note || undefined,
      params: Object.keys(paramsObj).length > 0 ? paramsObj : undefined,
      actionRefs: undefined,
    };

    const nodeData = (sortedNodes.find((n) => n.id === item.nodeId)?.data ?? {}) as Record<
      string,
      unknown
    >;
    // [legacy-playbook-source] the playbook editor reads/writes the node's configJson
    // playbook (via onUpdateNode → PUT node → configJson.playbookSteps). This is the
    // authoring surface; under the playbook→cache migration the cache row's
    // playbook_json is kept in sync from configJson at mint/heal. Phase-5 cutover moves
    // authoring onto the cache row directly. See the api getConfiguredPlaybookSteps note.
    const currentSteps = (nodeData.playbookSteps as PlaybookStep[] | undefined) ?? [];
    const newSteps = currentSteps.map((s) => (s.order === item.step.order ? updatedStep : s));

    setEditState(null);
    await onUpdateNode(item.nodeId, {
      playbookSteps: newSteps,
      playbookStepsUpdatedAt: new Date().toISOString(),
    });
  }, [editState, flatItems, sortedNodes, onUpdateNode]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditState(null);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        saveEdit();
      }
    },
    [saveEdit]
  );

  const handleDeleteStep = useCallback(
    async (item: FlatPlaybookItem) => {
      if (!onUpdateNode) return;
      // Clear edit state if deleting the step being edited
      if (editState?.itemId === item.id) setEditState(null);

      const nodeData = (sortedNodes.find((n) => n.id === item.nodeId)?.data ?? {}) as Record<
        string,
        unknown
      >;
      const currentSteps = (nodeData.playbookSteps as PlaybookStep[] | undefined) ?? [];
      const remaining = currentSteps.filter((s) => s.order !== item.step.order);

      if (remaining.length === 0) {
        await onUpdateNode(item.nodeId, {
          playbookSteps: [],
          playbookStepsUpdatedAt: null,
        });
      } else {
        // Reindex 1-based and clear actionRefs
        const reindexed = remaining.map((s, i) => ({
          ...s,
          order: i + 1,
          actionRefs: undefined,
        }));
        await onUpdateNode(item.nodeId, {
          playbookSteps: reindexed,
          playbookStepsUpdatedAt: new Date().toISOString(),
        });
      }
    },
    [editState?.itemId, sortedNodes, onUpdateNode]
  );

  const handleAddStep = useCallback(
    async (nodeId: string) => {
      if (!onUpdateNode) return;
      const nodeData = (sortedNodes.find((n) => n.id === nodeId)?.data ?? {}) as Record<
        string,
        unknown
      >;
      const currentSteps = (nodeData.playbookSteps as PlaybookStep[] | undefined) ?? [];
      const lastOrder = currentSteps.length > 0 ? Math.max(...currentSteps.map((s) => s.order)) : 0;

      const newStep: PlaybookStep = {
        order: lastOrder + 1,
        action: 'click',
        target: '',
      };

      // Set pending edit before updating so the effect picks it up after rerender
      setPendingEditStep({ nodeId, order: newStep.order });

      await onUpdateNode(nodeId, {
        playbookSteps: [...currentSteps, newStep],
        playbookStepsUpdatedAt: new Date().toISOString(),
      });
    },
    [sortedNodes, onUpdateNode]
  );

  // Build node index map for headers — include ALL non-end nodes so login/navigate etc. are visible
  const nodeIndexMap = new Map<string, number>();
  let nodeCounter = 0;
  for (const node of sortedNodes) {
    if (node.type === 'end') continue;
    nodeCounter++;
    nodeIndexMap.set(node.id, nodeCounter);
  }

  // Identify nodes without playbook steps (login, navigate, etc.) that need empty headers
  const nodesWithSteps = new Set(flatItems.map((fi) => fi.nodeId));
  const emptyNodes = sortedNodes.filter((n) => n.type !== 'end' && !nodesWithSteps.has(n.id));

  // Get screenshot URL for hovered item
  const hoveredItem = hoveredItemId ? flatItems.find((fi) => fi.id === hoveredItemId) : null;
  const hoveredAnchorRect = hoveredItemId
    ? (rowRefs.current.get(hoveredItemId)?.getBoundingClientRect() ?? null)
    : null;
  const hoveredScreenshotUrl = hoveredItem?.step.screenshotS3Key
    ? `${API_URL}/screenshots/${encodeURIComponent(hoveredItem.step.screenshotS3Key)}`
    : undefined;

  if (flatItems.length === 0 && emptyNodes.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Text size="sm" className="text-muted-foreground">
          No playbook steps recorded
        </Text>
      </div>
    );
  }

  // Build ordered list of node IDs for interleaving empty nodes between flatItems
  const nodeOrder = sortedNodes.filter((n) => n.type !== 'end').map((n) => n.id);

  // Track which nodeId we last rendered a header for
  let lastNodeId: string | null = null;
  let lastBranchLabel: 'true' | 'false' | null = null;
  let lastConditionNodeId: string | null = null;

  // Helper to render empty node headers (nodes without playbook steps)
  const renderEmptyNodes = (beforeNodeId: string | null) => {
    // Find empty nodes that should appear before the given nodeId
    const result: React.ReactNode[] = [];
    const beforeIdx = beforeNodeId ? nodeOrder.indexOf(beforeNodeId) : nodeOrder.length;
    for (const emptyNode of emptyNodes) {
      const emptyIdx = nodeOrder.indexOf(emptyNode.id);
      // Render empty nodes that come before this position and after lastNodeId
      const lastIdx = lastNodeId ? nodeOrder.indexOf(lastNodeId) : -1;
      if (emptyIdx > lastIdx && emptyIdx < beforeIdx) {
        const nodeType = emptyNode.type || 'unknown';
        const typeColorClass =
          NODE_TYPE_COLORS[nodeType] || 'bg-muted text-muted-foreground border-border';
        const stepNumber = nodeIndexMap.get(emptyNode.id) ?? 0;
        const depth = branchDepths.get(emptyNode.id) ?? 0;
        const indentStyle = depth > 0 ? { paddingLeft: `${depth * 1.5}rem` } : undefined;
        const emptyBranchInfo = branchInfoMap.get(emptyNode.id);

        let emptyBranchDivider: React.ReactNode = null;
        if (emptyBranchInfo?.type === 'branch-child') {
          const isNewBranch =
            emptyBranchInfo.conditionNodeId !== lastConditionNodeId ||
            emptyBranchInfo.branchLabel !== lastBranchLabel;
          if (isNewBranch) {
            const label = emptyBranchInfo.branchLabel === 'true' ? 'If true' : 'If false';
            emptyBranchDivider = (
              <div style={indentStyle}>
                <BranchDivider label={label} />
              </div>
            );
          }
          lastBranchLabel = emptyBranchInfo.branchLabel;
          lastConditionNodeId = emptyBranchInfo.conditionNodeId;
        } else if (emptyBranchInfo?.type === 'convergence') {
          emptyBranchDivider = (
            <div style={indentStyle}>
              <BranchDivider label="continues" />
            </div>
          );
          lastBranchLabel = null;
          lastConditionNodeId = null;
        } else if (emptyBranchInfo?.type === 'branch-start') {
          lastBranchLabel = null;
          lastConditionNodeId = null;
        }

        result.push(
          <React.Fragment key={`empty-${emptyNode.id}`}>
            {emptyBranchDivider}
            <div
              ref={(el) => {
                if (el) nodeHeaderRefs.current.set(emptyNode.id, el);
                else nodeHeaderRefs.current.delete(emptyNode.id);
              }}
              className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-3 py-2"
              style={indentStyle}
            >
              <Flex align="center" gap="sm">
                <button
                  type="button"
                  onClick={() => onNodeNavigate?.(emptyNode.id)}
                  className="flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0 hover:ring-2 hover:ring-primary hover:ring-offset-1 transition-shadow cursor-pointer"
                  title="Scroll to node"
                >
                  {stepNumber}
                </button>
                <Text size="sm" weight="medium" className="flex-1 min-w-0 truncate">
                  {getNodeTitle(emptyNode)}
                </Text>
                <NodeCacheSummary cache={cacheByNodeId?.get(emptyNode.id)} />
                <EffectivePlaybookSummary playbook={effectiveByNodeId?.get(emptyNode.id)} />
                <span
                  className={cn(
                    'text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0',
                    typeColorClass
                  )}
                >
                  {nodeType}
                </span>
              </Flex>
            </div>
            {editable && (
              <button
                type="button"
                className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors border-b border-border/50"
                style={indentStyle}
                onClick={() => handleAddStep(emptyNode.id)}
              >
                <Plus size={12} weight="bold" />
                Add step
              </button>
            )}
          </React.Fragment>
        );
        lastNodeId = emptyNode.id;
      }
    }
    return result;
  };

  return (
    <div className="relative">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(event: DragEndEvent) => {
          setIsDragging(false);
          handleDragEnd(event);
        }}
        onDragCancel={() => setIsDragging(false)}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {flatItems.map((item, itemIdx) => {
            const showHeader = item.nodeId !== lastNodeId;

            // Before rendering this node's header, render any empty nodes that come before it
            const emptyNodesBefore = showHeader ? renderEmptyNodes(item.nodeId) : [];

            lastNodeId = item.nodeId;

            const node = sortedNodes.find((n) => n.id === item.nodeId);
            const nodeType = node?.type || 'unknown';
            const typeColorClass =
              NODE_TYPE_COLORS[nodeType] || 'bg-muted text-muted-foreground border-border';
            const stepNumber = nodeIndexMap.get(item.nodeId) ?? 0;

            const isEditing = editState?.itemId === item.id;
            const isLastInNode =
              itemIdx === flatItems.length - 1 || flatItems[itemIdx + 1]?.nodeId !== item.nodeId;

            // Branch-aware rendering
            const branchInfo = branchInfoMap.get(item.nodeId);
            const depth = branchDepths.get(item.nodeId) ?? 0;
            const indentStyle = depth > 0 ? { paddingLeft: `${depth * 1.5}rem` } : undefined;

            let branchDivider: React.ReactNode = null;
            if (showHeader && branchInfo?.type === 'branch-child') {
              const isNewBranch =
                branchInfo.conditionNodeId !== lastConditionNodeId ||
                branchInfo.branchLabel !== lastBranchLabel;
              if (isNewBranch) {
                const label = branchInfo.branchLabel === 'true' ? 'If true' : 'If false';
                branchDivider = (
                  <div style={indentStyle}>
                    <BranchDivider label={label} />
                  </div>
                );
              }
              lastBranchLabel = branchInfo.branchLabel;
              lastConditionNodeId = branchInfo.conditionNodeId;
            } else if (showHeader && branchInfo?.type === 'convergence') {
              branchDivider = (
                <div style={indentStyle}>
                  <BranchDivider label="continues" />
                </div>
              );
              lastBranchLabel = null;
              lastConditionNodeId = null;
            } else if (showHeader && branchInfo?.type === 'branch-start') {
              lastBranchLabel = null;
              lastConditionNodeId = null;
            }

            return (
              <React.Fragment key={item.id}>
                {emptyNodesBefore}
                {showHeader && branchDivider}

                {showHeader && (
                  <div
                    ref={(el) => {
                      if (el) nodeHeaderRefs.current.set(item.nodeId, el);
                      else nodeHeaderRefs.current.delete(item.nodeId);
                    }}
                    className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-3 py-2"
                    style={indentStyle}
                  >
                    <Flex align="center" gap="sm">
                      <button
                        type="button"
                        onClick={() => onNodeNavigate?.(item.nodeId)}
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0 hover:ring-2 hover:ring-primary hover:ring-offset-1 transition-shadow cursor-pointer"
                        title="Scroll to node"
                      >
                        {stepNumber}
                      </button>
                      <Text size="sm" weight="medium" className="flex-1 min-w-0 truncate">
                        {node ? getNodeTitle(node) : 'Unknown'}
                      </Text>
                      <NodeCacheSummary cache={cacheByNodeId?.get(item.nodeId)} />
                      <EffectivePlaybookSummary playbook={effectiveByNodeId?.get(item.nodeId)} />
                      <span
                        className={cn(
                          'text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0',
                          typeColorClass
                        )}
                      >
                        {nodeType}
                      </span>
                    </Flex>
                  </div>
                )}

                <div
                  ref={(el) => {
                    if (el) rowRefs.current.set(item.id, el);
                    else rowRefs.current.delete(item.id);
                  }}
                  style={indentStyle}
                >
                  {isEditing ? (
                    <div
                      className="px-3 py-2 bg-primary/5 ring-1 ring-primary/30 space-y-2"
                      onKeyDown={handleKeyDown}
                    >
                      <Flex gap="sm" align="center">
                        <div className="w-28">
                          <Select
                            aria-label="Action type"
                            items={ACTION_OPTIONS}
                            selectedKey={editState.action}
                            onSelectionChange={(key) =>
                              setEditState((prev) =>
                                prev ? { ...prev, action: key as PlaybookStepAction } : null
                              )
                            }
                          />
                        </div>
                        <div className="flex-1">
                          <TextField
                            aria-label="Target"
                            value={editState.target}
                            onChange={(value) =>
                              setEditState((prev) => (prev ? { ...prev, target: value } : null))
                            }
                            placeholder="Target selector or description"
                            autoFocus
                          />
                        </div>
                      </Flex>

                      {editState.params.length > 0 && (
                        <div className="space-y-1">
                          <Text size="xs" className="text-muted-foreground">
                            Params
                          </Text>
                          {editState.params.map((p, idx) => (
                            <Flex key={idx} gap="sm">
                              <TextField
                                aria-label={`Param ${idx + 1} key`}
                                value={p.key}
                                onChange={(value) =>
                                  setEditState((prev) => {
                                    if (!prev) return null;
                                    const params = [...prev.params];
                                    params[idx] = { ...params[idx], key: value };
                                    return { ...prev, params };
                                  })
                                }
                                placeholder="key"
                                className="flex-1"
                              />
                              <TextField
                                aria-label={`Param ${idx + 1} value`}
                                value={p.value}
                                onChange={(value) =>
                                  setEditState((prev) => {
                                    if (!prev) return null;
                                    const params = [...prev.params];
                                    params[idx] = { ...params[idx], value: value };
                                    return { ...prev, params };
                                  })
                                }
                                placeholder="value"
                                className="flex-1"
                              />
                            </Flex>
                          ))}
                        </div>
                      )}

                      <TextField
                        aria-label="Note"
                        value={editState.note}
                        onChange={(value) =>
                          setEditState((prev) => (prev ? { ...prev, note: value } : null))
                        }
                        placeholder="Note (optional)"
                      />

                      <Flex gap="sm" justify="end">
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                          onClick={() => setEditState(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="text-xs text-primary hover:text-primary/80 font-medium px-2 py-1"
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                      </Flex>
                    </div>
                  ) : (
                    <>
                      <SortablePlaybookRow
                        sortableId={item.id}
                        step={item.step}
                        isSelected={false}
                        onSelect={() => handleSelect(item)}
                        onHover={() => handleHover(item.id)}
                        onLeave={handleLeave}
                        onDelete={() => handleDeleteStep(item)}
                        isHovered={hoveredItemId === item.id}
                        readOnly={!editable}
                      />
                      <StepCacheRow
                        info={resolveStepCache(cacheByNodeId, item.nodeId, item.step)}
                      />
                    </>
                  )}
                </div>

                {isLastInNode && editable && (
                  <>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors border-b border-border/50"
                      style={indentStyle}
                      onClick={() => handleAddStep(item.nodeId)}
                    >
                      <Plus size={12} weight="bold" />
                      Add step
                    </button>
                  </>
                )}
              </React.Fragment>
            );
          })}
          {/* Render any trailing empty nodes that come after all flatItems */}
          {renderEmptyNodes(null)}
        </SortableContext>
      </DndContext>

      {/* Screenshot popover — suppressed during drag */}
      {!isDragging &&
        hoveredAnchorRect &&
        hoveredItem &&
        (hoveredScreenshotUrl ? (
          <ScreenshotPopover
            screenshotUrl={hoveredScreenshotUrl}
            anchorRect={hoveredAnchorRect}
            step={hoveredItem.step}
            nodeId={hoveredItem.nodeId}
            cacheByNodeId={cacheByNodeId}
            onDeleteNodeCache={onDeleteNodeCache}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handleLeave}
          />
        ) : (
          <NoScreenshotPopover
            anchorRect={hoveredAnchorRect}
            step={hoveredItem.step}
            nodeId={hoveredItem.nodeId}
            cacheByNodeId={cacheByNodeId}
            onDeleteNodeCache={onDeleteNodeCache}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handleLeave}
          />
        ))}
    </div>
  );
}
